import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import IconButton from "../../components/IconButton/IconButton";
import CategoryTile from "../../components/CategoryTile/CategoryTile";
import Grid from "../../config/Grid/Grid";
import styles from "./Menu.module.scss";
import { useMenuData } from "@/hooks/useMenuData";
import { ALL_MENU_TYPE_ID, menuTypePath, tablePath } from "@/routes/paths";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";
import { useGoBack } from "@/hooks/useGoBack";

/** Browse-only menu: pick a menu. The orderable twin lives under /order. */
function Menu() {
  const { restaurantId = "" } = useParams<{ restaurantId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tableId = useVenuesDataStore((state) => state.tableId);
  const goBack = useGoBack(
    tableId ? tablePath(restaurantId, tableId) : undefined,
  );

  const { dishes, menuTypeGroups, loading, error } = useMenuData(restaurantId);

  // Nothing to choose between: open the whole list rather than showing a
  // single tile. Mirrors OrderMenuTypesPage.
  if (!loading && !error && menuTypeGroups.length === 0 && dishes.length > 0) {
    return (
      <Navigate to={menuTypePath(restaurantId, ALL_MENU_TYPE_ID)} replace />
    );
  }

  return (
    <Grid>
      <div className={`start-1 col-4 ${styles.menuWrapper}`}>
        <div className={styles.menuHeaderContainer}>
          <div className={styles.menuHeader}>
            <IconButton
              iconName="las la-angle-left icon-big"
              onClick={goBack}
              color="primary"
              variant="text"
            />
            <p className={`body-bold-large ${styles.menuTitle}`}>
              {t("menu.title")}
            </p>
            <IconButton
              iconName=""
              onClick={() => {}}
              color="primary"
              variant="text"
            />
          </div>
        </div>
        <div className={styles.menuContent}>
          {error ? (
            <p className={`body-medium ${styles.menuStateMessage}`}>
              {t("order.loadError")}
            </p>
          ) : null}
          {!error && !loading && dishes.length === 0 ? (
            <p className={`body-medium ${styles.menuStateMessage}`}>
              {t("order.emptyMenu")}
            </p>
          ) : null}
          <div className={styles.categoryGrid}>
            {/* Reaches dishes filed under no menu type, which no tile covers. */}
            {dishes.length > 0 ? (
              <CategoryTile
                name={t("menu.allDishes")}
                subtitle={t("menu.dishCount", { count: dishes.length })}
                onClick={() =>
                  navigate(menuTypePath(restaurantId, ALL_MENU_TYPE_ID))
                }
              />
            ) : null}
            {menuTypeGroups.map((group) => (
              <CategoryTile
                key={group.menuType.id}
                name={group.name}
                subtitle={t("menu.dishCount", { count: group.dishes.length })}
                onClick={() =>
                  navigate(menuTypePath(restaurantId, group.menuType.id))
                }
              />
            ))}
          </div>
        </div>
      </div>
    </Grid>
  );
}

export default Menu;
